import { A } from '@solidjs/router'
import { type Component, For, type JSX, Show } from 'solid-js'
import { Badge } from '../../primitives/Badge'
import { createScrollEdges } from '../../primitives/ScrollArea/createScrollEdges'
import { createTabIndicator } from './createTabIndicator'
import { tabsEnd } from './tabsEnd'
import './Tabs.css'
import './navSelected.css'

export interface TabsNavItem {
  value: string
  label: string
  icon?: JSX.Element
  count?: number
  trailing?: JSX.Element
  href?: string
  disabled?: boolean
}

export interface TabsNavProps {
  items: TabsNavItem[]
  value: string
  onChange?: (value: string) => void
  'aria-label'?: string
  class?: string
}

export const TabsNav: Component<TabsNavProps> = (props) => {
  const indicator = createTabIndicator(() => props.value)
  const edges = createScrollEdges('x', undefined, tabsEnd)
  const linkMode = () => props.items.some((item) => item.href != null)

  const els = new Map<string, HTMLElement>()

  const attachList = (el: HTMLElement) => {
    indicator.setListEl(el)
    edges.attach(el)
  }

  const registerTrigger = (value: string, el: HTMLElement) => {
    indicator.registerTrigger(value, el)
    edges.observe(el)
  }

  const makeList = () => (
    <For each={props.items}>
      {(item) => {
        const selected = () => props.value === item.value
        const linkHref = () => (item.disabled ? undefined : item.href)
        return (
          <Show
            when={linkHref()}
            fallback={
              <button
                type="button"
                role="tab"
                ref={(el) => {
                  registerTrigger(item.value, el)
                  els.set(item.value, el)
                }}
                aria-selected={selected()}
                data-selected={selected() ? '' : undefined}
                tabIndex={selected() ? 0 : -1}
                disabled={item.disabled ?? false}
                class="ds-tab"
                onClick={() => {
                  if (!item.disabled) {
                    props.onChange?.(item.value)
                  }
                }}
              >
                <span class="ds-tab-content">
                  <Show when={item.icon}>
                    <span class="ds-tab-ico">{item.icon}</span>
                  </Show>
                  {item.label}
                  <Show when={item.count != null}>
                    <Badge size="sm" variant="neutral" class="ds-tab-count">
                      {item.count}
                    </Badge>
                  </Show>
                  {item.trailing}
                </span>
              </button>
            }
          >
            {(href) => (
              <A
                href={href()}
                ref={(el) => registerTrigger(item.value, el)}
                aria-current={selected() ? 'page' : undefined}
                data-selected={selected() ? '' : undefined}
                class="ds-tab"
                onClick={() => props.onChange?.(item.value)}
              >
                <span class="ds-tab-content">
                  <Show when={item.icon}>
                    <span class="ds-tab-ico">{item.icon}</span>
                  </Show>
                  {item.label}
                  <Show when={item.count != null}>
                    <Badge size="sm" variant="neutral" class="ds-tab-count">
                      {item.count}
                    </Badge>
                  </Show>
                  {item.trailing}
                </span>
              </A>
            )}
          </Show>
        )
      }}
    </For>
  )

  const makePill = () => (
    <Show when={indicator.pill()}>
      {(p) => (
        <div
          ref={indicator.setIndicatorEl}
          class="ds-tab-indicator"
          classList={{
            'ds-tab-indicator--left': indicator.direction() === 'left',
            'ds-tab-indicator--right': indicator.direction() === 'right',
          }}
          aria-hidden="true"
          style={{ transform: `translateX(${p().x}px)`, width: `${p().w}px` }}
        >
          <div
            class="ds-tab-glass ds-nav-selected"
            classList={{ 'is-moving': indicator.moving() }}
          />
        </div>
      )}
    </Show>
  )

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return
    }
    event.preventDefault()

    const enabledItems = props.items.filter((item) => !item.disabled)
    if (enabledItems.length === 0) {
      return
    }

    const currentIndex = enabledItems.findIndex((item) => item.value === props.value)
    let nextIndex: number

    if (event.key === 'ArrowRight') {
      nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % enabledItems.length
    } else {
      nextIndex =
        currentIndex === -1
          ? enabledItems.length - 1
          : (currentIndex - 1 + enabledItems.length) % enabledItems.length
    }

    const nextItem = enabledItems[nextIndex]
    if (!nextItem) {
      return
    }
    props.onChange?.(nextItem.value)
    els.get(nextItem.value)?.focus()
  }

  return (
    <Show
      when={linkMode()}
      fallback={
        <div
          role="tablist"
          aria-label={props['aria-label']}
          ref={attachList}
          data-scroll-start={edges.start() ? '' : undefined}
          data-scroll-end={edges.end() ? '' : undefined}
          class={`ds-tabs-list ${props.class ?? ''}`}
          classList={{ 'ds-tabs-list--glide': indicator.glide() }}
          onKeyDown={handleKeyDown}
        >
          {makePill()}
          {makeList()}
        </div>
      }
    >
      <nav
        aria-label={props['aria-label']}
        ref={attachList}
        data-scroll-start={edges.start() ? '' : undefined}
        data-scroll-end={edges.end() ? '' : undefined}
        class={`ds-tabs-list ${props.class ?? ''}`}
        classList={{ 'ds-tabs-list--glide': indicator.glide() }}
      >
        {makePill()}
        {makeList()}
      </nav>
    </Show>
  )
}
